import { Component, ViewEncapsulation, OnInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { VehicleBlFormService } from '../../../services/bl-form/vehicle-bl-form.service';
import { PaginatedResponse, PaginationInfo } from '../../../shared/models/api-response.interface';
import { DestructorComponent } from '../../../components/destructor/destructor.component';
import { ViewBlFormComponent } from '../../../layout/modals/shipping_and_documents/view-bl-form/view-bl-form.component';

@Component({
  selector: 'app-bl-amend',
  templateUrl: './bl-amend.component.html',
  styleUrls: ['./bl-amend.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BlAmendComponent extends DestructorComponent implements OnInit {

  @ViewChild('viewBlFormModal') viewBlFormModal!: ViewBlFormComponent;

  // Data properties
  blAmendData: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  isSaving: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  hasChanges: boolean = false;
  changedRows: any[] = [];

  // Selection properties
  selectedItems: Set<string> = new Set();

  // Form data for editable fields
  formData: { [blFormId: string]: any } = {};

  constructor(
    private _vehicleBlFormService: VehicleBlFormService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadBlAmendData();
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  loadBlAmendData(page: number = 1, search: string = ''): void {
    this.isLoading = true;
    const filters: Record<string, any> = {};

    this.subscriptions.push(
      this._vehicleBlFormService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.blAmendData = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
            this.initializeFormData();
          } else {
            this.blAmendData = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading BL Amend data:', error);
          this.blAmendData = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  private initializeFormData(): void {
    this.formData = {};
    this.blAmendData.forEach(item => {
      this.formData[item._id] = {
        ar: item.ar || false,
        bl_memo: item.bl_memo || ''
      };
    });
  }

  onPageChange(page: number): void {
    this.loadBlAmendData(page, this.searchTerm);
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadBlAmendData(1, this.searchTerm);
  }

  onFieldChange(itemId: string, field: string, value: any): void {
    if (!this.formData[itemId]) {
      this.formData[itemId] = {};
    }

    this.formData[itemId][field] = value;

    // Check if this item is already in changed rows
    const existingIndex = this.changedRows.findIndex(row => row._id === itemId);

    if (existingIndex >= 0) {
      // Update existing changed row
      this.changedRows[existingIndex] = {
        _id: itemId,
        ...this.formData[itemId]
      };
    } else {
      // Add new changed row
      this.changedRows.push({
        _id: itemId,
        ...this.formData[itemId]
      });
    }

    this.hasChanges = this.changedRows.length > 0;
  }

  onSave(): void {
    if (this.changedRows.length === 0) {
      return;
    }

    const payload = {
      bl_forms: this.changedRows
    };

    this.isSaving = true;

    this._vehicleBlFormService.BulkUpdate(payload).subscribe({
      next: (response) => {
        console.log('BL Amend data saved successfully:', response);
        // Reset changes after successful save
        this.hasChanges = false;
        this.changedRows = [];
        this.isSaving = false;
        this.loadBlAmendData(this.currentPage, this.searchTerm);
      },
      error: (error) => {
        console.error('Error saving BL Amend data:', error);
        this.isSaving = false;
      }
    });
  }

  // Icon click handlers
  onPriceSheetClick(item: any): void {
    console.log('Price Sheet clicked for:', item);
  }

  onDocsClick(item: any): void {
    console.log('Docs clicked for:', item);
  }

  // Helper methods
  getDisplayValue(value: any, fallback: string = '-'): string {
    return value || fallback;
  }

  getFormValue(itemId: string, field: string): any {
    return this.formData[itemId]?.[field] || (field === 'ar' ? false : '');
  }

  setFormValue(itemId: string, field: string, value: any): void {
    if (!this.formData[itemId]) {
      this.formData[itemId] = {};
    }
    this.formData[itemId][field] = value;
    this.onFieldChange(itemId, field, value);
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): string {
    return item._id || index.toString();
  }

  // Selection methods
  onItemSelect(itemId: string, event: any): void {
    if (event.target.checked) {
      this.selectedItems.add(itemId);
    } else {
      this.selectedItems.delete(itemId);
    }
  }

  onSelectAll(event: any): void {
    if (event.target.checked) {
      this.blAmendData.forEach(item => this.selectedItems.add(item._id));
    } else {
      this.selectedItems.clear();
    }
  }

  isItemSelected(itemId: string): boolean {
    return this.selectedItems.has(itemId);
  }

  isAllSelected(): boolean {
    return this.blAmendData.length > 0 && this.selectedItems.size === this.blAmendData.length;
  }

  isIndeterminate(): boolean {
    return this.selectedItems.size > 0 && this.selectedItems.size < this.blAmendData.length;
  }

  getSelectedCount(): number {
    return this.selectedItems.size;
  }

  // Copy selected ISL numbers to clipboard
  copySelectedISL(): void {
    if (this.selectedItems.size === 0) {
      return;
    }

    // Get car_no values for selected items
    const selectedItemIds = Array.from(this.selectedItems);
    const selectedCarNos = this.blAmendData
      .filter(item => selectedItemIds.includes(item._id))
      .map(item => item.order?.vehicles?.[0]?.car_no)
      .filter(carNo => carNo) // Remove empty values
      .join(',');

    if (selectedCarNos) {
      // Copy to clipboard
      navigator.clipboard.writeText(selectedCarNos).then(() => {
        console.log('ISL numbers copied to clipboard:', selectedCarNos);
        alert(`${this.selectedItems.size} ISL numbers copied to clipboard!`);
      }).catch(err => {
        console.error('Failed to copy to clipboard:', err);
        alert('Failed to copy to clipboard. Please try again.');
      });
    } else {
      alert('No ISL numbers found for selected items.');
    }
  }

  // Open View BL Form Modal
  openViewBlFormModal(item: any, amendmentNumber: number): void {
    if (this.viewBlFormModal) {
      this.viewBlFormModal.openModal(item, amendmentNumber);
    }
  }
}
